<template>
	<div style="position: relative">
		<div style="display: flex; flex-direction: column; gap: 10px">
			<div style="display: flex; align-items: center; gap: 12px">
				<div>
					<button
						class="panel-close-button"
						@click="$emit('closePanel')"
						:title="$t('features.articlePanel.minimize')"
					>
						<svg-icon type="mdi" :path="mdiChevronRight"></svg-icon>
					</button>
				</div>
				<div>
					<h4>{{ article.description }}</h4>
				</div>
			</div>

			<div><strong>{{ $t("features.articlePanel.blexonNumber") }} </strong>{{ article.id }}</div>
			<div>
				<strong>{{ $t("features.articlePanel.material") }} </strong>{{ store.validationParameter.materialBezeichnung }}
			</div>
			<div>
				<strong>{{ $t("features.articlePanel.dimensions") }} </strong>{{ store.cam.model.length.toFixed(2) }} x
				{{ store.cam.model.width.toFixed(2) }} x
				{{ store.cam.model.height.toFixed(2) }} [mm]
			</div>
			<div style="display: flex; flex-direction: row; align-items: center; gap: 4px">
				<div style="height: 20px; width: 20px; background-color: #46a0cd"></div>
				<div>{{ $t("common.featureType.anchorFace") }}{{ getZusatzbezeichnungOberseite() }}</div>
			</div>
			<div style="display: flex; flex-direction: row; align-items: center; gap: 4px">
				<div style="height: 20px; width: 20px; background-color: #8ca0aa"></div>
				<div>{{ $t("common.featureType.bottomFace") }}{{ getZusatzbezeichnungUnterseite() }}</div>
			</div>

			<div style="text-align: center">
				<button
					class="blexon-button"
					@click="$emit('saveAndClose')"
					:disabled="
						store.isFileSaved ||
						store.article.isReadOnly ||
						(store.article.isLocked && !store.article.hasElevatedRights)
					"
					style="width: 100%"
				>
					{{ $t("features.articlePanel.saveAndClose") }}
				</button>
			</div>
			<div style="text-align: center">
				<button
					v-if="hasErrorMessages"
					class="blexon-button"
					@click="sendToHelpdeskDialogVisible = true"
					:disabled="store.article.isLocked"
					style="width: 100%"
				>
					{{
						store.article.isLocked
							? $t("features.articlePanel.underReview")
							: $t("features.articlePanel.sendToHelpdesk")
					}}
				</button>
			</div>
		</div>

		<SendToHelpdeskDialog
			v-if="sendToHelpdeskDialogVisible"
			@close="sendToHelpdeskDialogVisible = false"
			@sendToHelpdesk="sendToHelpdesk"
		/>
	</div>
</template>

<script setup lang="ts">
import { useMainStore } from "@/store/mainStore";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiChevronRight } from "@mdi/js";
import { MessageState, Schweregrad } from "@/model/Enums";
import { IArticle } from "@/model/IArticle";
import SendToHelpdeskDialog from "@/components/SendToHelpdeskDialog.vue";

const emit = defineEmits(["saveAndClose", "closePanel"]);

const store = useMainStore();
const { t } = useI18n();
const sendToHelpdeskDialogVisible = ref<boolean>(false);
const article = computed((): IArticle => {
	return store.article;
});
const hasErrorMessages = computed((): boolean => {
	return store.validationMessages.some(
		(message) => message.severity === Schweregrad.Error && message.state === MessageState.Open
	);
});

const sendToHelpdesk = () => {
	sendToHelpdeskDialogVisible.value = false;
	emit("saveAndClose");
};

const getZusatzbezeichnungOberseite = () => {
	const material = store.validationParameter;
	if (material && material.materialBezeichnung) {
		if (material.materialBezeichnung.includes("geschliffen")) {
			return t("features.articlePanel.additionalDesignation.groundFoil");
		}
		if (material.materialBezeichnung.includes("eloxiert")) {
			return t("features.articlePanel.additionalDesignation.foil");
		}
	}
	return "";
};
const getZusatzbezeichnungUnterseite = () => {
	const material = store.validationParameter;
	if (material && material.materialBezeichnung) {
		if (material.materialBezeichnung.includes("Riffel")) {
			return t("features.articlePanel.additionalDesignation.ribbed");
		}
	}
	return "";
};
</script>
